import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Flame, ArrowUpRight } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";

/**
 * Compact "Hot Prospects" widget for the Dashboard.
 * Lists the top 5 HOT companies (recent activity or active hot-stage deal),
 * with a link to the company detail page.
 *
 * Auto-detects: if /temperature/companies returns no HOT bucket, the widget
 * gracefully renders a friendly empty state with guidance.
 */
export default function HotProspectsWidget() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/temperature/companies?temperature=HOT");
        if (!alive) return;
        setItems(Array.isArray(data) ? data.slice(0, 5) : []);
      } catch (e) {
        if (!alive) return;
        setError(e);
        setItems([]);
      }
    })();
    return () => { alive = false; };
  }, []);

  return (
    <Card className="border-border" data-testid="hot-prospects-widget">
      <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
        <div className="flex items-center gap-2">
          <span className="h-7 w-7 rounded-md bg-red-500/10 border border-red-500/20 flex items-center justify-center">
            <Flame className="h-3.5 w-3.5 text-red-600" />
          </span>
          <CardTitle className="text-sm font-medium">
            {t("dashboard.hot_prospects", "Hot Prospects")}
          </CardTitle>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 text-[11px] text-muted-foreground hover:text-foreground"
          onClick={() => navigate("/companies?temperature=HOT")}
          data-testid="hot-prospects-see-all"
        >
          {t("dashboard.see_all", "Tout voir")} <ArrowUpRight className="h-3 w-3 ml-1" />
        </Button>
      </CardHeader>

      <CardContent className="pt-1">
        {items === null ? (
          <div className="space-y-2">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-9 rounded bg-muted/40 animate-pulse" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-6">
            <div className="text-xs text-muted-foreground mb-1">
              {t("dashboard.hot_empty_title", "Pas encore de prospects chauds")}
            </div>
            <div className="text-[11px] text-muted-foreground/70">
              {t(
                "dashboard.hot_empty_hint",
                "Une société devient HOT après une activité récente (< 7 j) ou un deal en proposition/négociation."
              )}
            </div>
            {error ? null : null}
          </div>
        ) : (
          <ul className="space-y-1">
            {items.map((c) => (
              <li
                key={c.company_id}
                className="group flex items-center justify-between gap-2 px-2 py-1.5 rounded-md hover:bg-muted/60 cursor-pointer transition-colors"
                onClick={() => navigate(`/companies/${c.company_id}`)}
                data-testid={`hot-prospect-${c.company_id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] font-medium truncate">{c.name}</div>
                  <div className="text-[10.5px] text-muted-foreground">
                    {c.has_hot_stage
                      ? t("dashboard.hot_reason_stage", "Deal en négociation")
                      : c.days_since_activity != null
                      ? t("dashboard.hot_reason_recent", "Activité il y a {{n}} j", { n: c.days_since_activity })
                      : t("dashboard.hot_reason_new", "Nouveau signal")}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {c.ai_score != null ? (
                    <Badge variant="secondary" className="text-[10px] h-5 px-1.5 bg-emerald-50 text-emerald-700 border-emerald-200">
                      {c.ai_score}
                    </Badge>
                  ) : null}
                  <Flame className="h-3.5 w-3.5 text-red-500" />
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
